import { useState, useMemo, useEffect } from 'react';
import { List, useListContext, CreateButton, TopToolbar, useRedirect, useNotify, useRefresh } from 'react-admin';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TextField, InputAdornment, Chip, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import authFetch from '../utils/authFetch';
import { tbl, btn, fmtQty, PURCHASE_STATUS, inputSx, clickableA11y } from '../styles/ds';
import { useFitCount } from '../hooks/useFitCount';
import FitPagination from '../components/FitPagination';

const Toolbar = () => (
  <TopToolbar><CreateButton label="רכישה חדשה" sx={btn.create} /></TopToolbar>
);

const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('he-IL') : '—';
const fmtCost = (amount, currency) => currency === 'USD'
  ? `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  : `₪${Number(amount || 0).toLocaleString('he-IL', { minimumFractionDigits: 2 })}`;

const PurchaseTable = () => {
  const { data = [], isLoading } = useListContext();
  const redirect = useRedirect();
  const notify   = useNotify();
  const refresh  = useRefresh();
  const [search, setSearch] = useState('');
  const [receiving, setReceiving] = useState(null);
  const [page, setPage] = useState(1);
  const { fitCount, renderCount, containerRef, startIndex } = useFitCount({ fallback: 10, page });

  const receive = async (e, p) => {
    e.stopPropagation();
    setReceiving(p.id);
    try {
      const res  = await authFetch(`/purchases/${p.id}/receive`, { method: 'POST' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'שגיאה בקליטת הסחורה');
      notify('הסחורה נקלטה למלאי', { type: 'success' });
      refresh();
    } catch (err) {
      notify(err.message, { type: 'error' });
    } finally {
      setReceiving(null);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.trim().toLowerCase();
    return data.filter(p =>
      (p.variant_name || '').toLowerCase().includes(q) ||
      (p.supplier_name || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  useEffect(() => { setPage(1); }, [search]);

  const pageItems = useMemo(() => {
    return filtered.slice(startIndex, startIndex + renderCount);
  }, [filtered, startIndex, renderCount]);

  if (isLoading) return null;
  return (
    <Box>
      <Box className="crm-list-search" px={1} py={1.5}>
        <TextField size="small" placeholder="חיפוש לפי מוצר או ספק..." aria-label="חיפוש רכישות"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment>, sx: { ...inputSx, direction: 'rtl' } }}
          sx={{ width: 300 }} />
      </Box>
    <TableContainer component={Box} sx={{ ...tbl.container, border: '1px solid rgba(13,29,62,0.06)' }}>
      <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table>
        <TableHead><TableRow>
          <TableCell sx={{ width: 60 }}>#</TableCell>
          <TableCell>מוצר</TableCell>
          <TableCell>ספק</TableCell>
          <TableCell align="center">כמות</TableCell>
          <TableCell align="right">עלות ליחידה</TableCell>
          <TableCell align="right">סה"כ עלות</TableCell>
          <TableCell align="center">סטטוס</TableCell>
          <TableCell align="center">תאריך</TableCell>
        </TableRow></TableHead>
        <TableBody ref={containerRef}>
          {pageItems.map((p, i) => {
            const st = PURCHASE_STATUS[p.status] || PURCHASE_STATUS.received;
            return (
            <TableRow key={p.id} onClick={() => redirect('edit', 'purchases', p.id)}
              {...clickableA11y(() => redirect('edit', 'purchases', p.id), { ariaLabel: `פתיחת רכישה מספר ${p.id}` })}
              sx={tbl.bodyRow(i)}>
              <TableCell><Typography fontWeight={600} color="text.secondary" fontSize={13}>#{p.id}</Typography></TableCell>
              <TableCell><Typography fontWeight={600} fontSize={14}>{p.variant_name || `וריאנט #${p.variant_id}`}</Typography></TableCell>
              <TableCell><Typography fontSize={13} color="text.secondary">{p.supplier_name || '—'}</Typography></TableCell>
              <TableCell align="center"><Typography fontWeight={600}>{fmtQty(p.quantity, p.unit)}</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight={600} color="primary.main">{fmtCost(p.unit_cost, p.currency)}</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight={700} color="primary.main">{fmtCost(p.unit_cost * Number(p.quantity), p.currency)}</Typography></TableCell>
              <TableCell align="center">
                {p.status === 'ordered' ? (
                  <Button size="small" variant="contained" disabled={receiving === p.id}
                    startIcon={<InventoryIcon sx={{ fontSize: 14 }} />}
                    onClick={e => receive(e, p)}
                    sx={{ ...btn.primary, fontSize: 11, py: 0.3 }}>
                    קלוט למלאי
                  </Button>
                ) : (
                  <Chip label={st.label} size="small"
                    sx={{ height: 22, fontSize: 11, fontWeight: 700, backgroundColor: st.bg, color: st.color }} />
                )}
              </TableCell>
              <TableCell align="center"><Typography fontSize={13} color="text.secondary">{fmtDate(p.purchase_date)}</Typography></TableCell>
            </TableRow>
            );
          })}
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>{search ? 'לא נמצאו תוצאות' : 'אין רכישות עדיין'}</TableCell></TableRow>
          )}
        </TableBody>
      </Table></Box>
    </TableContainer>
    <FitPagination page={page} setPage={setPage} total={filtered.length} perPage={fitCount} />
    </Box>
  );
};

const PurchaseList = () => (
  <List className="crm-mobile-list crm-inline-list" sort={{ field: 'id', order: 'DESC' }} perPage={1000} pagination={false} storeKey={false} actions={<Toolbar />} title="רכישות" sx={tbl.listSx}>
    <PurchaseTable />
  </List>
);
export default PurchaseList;
